import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import JoditEditor from "jodit-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import RichTextEditor from "../../RichTextEditor";

const API_URL = "https://api.muktihospital.com/api/page";

const EditPage = () => {
  const { id } = useParams(); // Get the page ID from URL
  const navigate = useNavigate();
  const editor = useRef(null);

  // ✅ State for form data
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    translations: {
      en: { metaTitle: "", metaDescription: "", content: "" },
      bn: { metaTitle: "", metaDescription: "", content: "" },
    },
  });

  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Fetch existing page data
  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/${id}`, {
          headers: { "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079" },
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Failed to fetch page");

        console.log("✅ Existing Page Data:", data);
        setFormData(data);
      } catch (error) {
        setError(error.message);
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [id]);

  // ✅ Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [language]: { ...prev.translations[language], [name]: value },
      },
    }));
  };

  // ✅ Handle slug change manually
  const handleSlugChange = (e) => {
    setFormData((prev) => ({ ...prev, slug: e.target.value }));
  };

  // ✅ Handle language switch
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'bn', label: 'Bangla' },
  ];

  const handleLanguageChange = (selectedOption) => {
    setLanguage(selectedOption.value); // Update the current language
  };

  // ✅ Handle content change in Jodit Editor
  const handleContentChange = (newContent) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [language]: { ...prev.translations[language], content: newContent },
      },
    }));
  };

  // ✅ Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const response = await fetch(`${API_URL}/edit/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to update page");

      toast.success("Page updated successfully!");
      navigate("/all-page"); // Redirect to the pages list
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setUpdating(false);
    }
  };


  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Pages"},
    { label: "Edit Page" },
  ];

  return (
    <div>
      <PageHeading title="Edit Page" breadcrumbs={breadcrumbs} />
      {error && <p className="text-red-600 bg-red-100 p-3 rounded">{error}</p>}
      
      {/* Language switcher */}
      <div className="mb-4 flex justify-end">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find(option => option.value === language)}
          isSearchable
        />
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="bg-white shadow-sm rounded-lg">
             <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                    <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                      Basic Information
                    </h2>
              </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
              {/* ✅ Page Name */}
            <div>
              <label className="label">Page Name</label>
              {loading ? <Skeleton height={40} /> : (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  className="input-field"
                  disabled
                />
              )}
            </div>

            {/* ✅ Slug */}
            <div>
              <label className="label">Slug</label>
              {loading ? <Skeleton height={40} /> : (
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleSlugChange}
                  className="input-field"
                  required
                />
              )}
            </div>
            </div>
              {/* ✅ Content (JoditEditor) */}
              <div className="px-5 mb-5">
              <label className="label">Content</label>
              {loading ? <Skeleton height={300} /> : (
                <RichTextEditor
                  value={formData.translations[language]?.content || ""}
                  onChange={handleContentChange}
                />
              )}
            </div>
             </div>
         <div>
            {/* SEO Configuration */}
         <div className="bg-white shadow-sm rounded-lg">
             <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  SEO Configuration
                </h2>
              </div>
            <div className="p-5 grid grid-cols-2 gap-6">
              {/* Meta Title */}
          <div>
            <label className="label">Meta Title</label>
            <input
              type="text"
              name="metaTitle"
              className="input-field"
              placeholder="Enter Meta Title"
              value={formData.translations[language]?.metaTitle || ""}
              onChange={handleInputChange}
            />
          </div>

          {/* Meta Description */}
          <div>
            <label className="label">Meta Description</label>
            <input
              type="text"
              name="metaDescription"
              className="input-field"
              placeholder="Enter Meta Description"
              value={formData.translations[language]?.metaDescription || ""}
              onChange={handleInputChange}
            />
          </div>
            </div>
          </div>
              {/* Submit */}
         <div className="mt-6 flex gap-4 justify-end">
              <button
                type="submit"
                className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
                disabled={loading}
              >
                Submit
              </button>
            </div>
        </div>
        </div>
      </form>
    </div>
  );
};

export default EditPage;
